import * as ToastPrimitive from '@radix-ui/react-toast';
import * as Sentry from '@sentry/nextjs';
import { RainbowKitProvider, DisclaimerComponent } from '@rainbow-me/rainbowkit';
import '@rainbow-me/rainbowkit/styles.css';
import { SSRProvider as ReactAriaSSRProvider } from '@react-aria/ssr';
import { QueryClientProvider } from '@tanstack/react-query';
import { SpeedInsights } from '@vercel/speed-insights/next';
import Decimal from 'decimal.js-light';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { WagmiProvider } from 'wagmi';

import { ErrorBoundary } from '@/components/App/ErrorBoundary';
import { containerStyles } from '@/components/App/index.css';
import { IntercomProvider } from '@/components/IntercomProvider';
import AppUpdater from '@/components/Updater/AppUpdater';
import TransactionUpdater from '@/components/Updater/TransactionUpdater';

import { useHideShowIntercom } from '@/hooks/useHideShowIntercom';

import { referrersStoreSelectors, useReferrerStore } from '@/store/referrals/';

import '@/styles/globals.css';
import { breakpoints, darkModeClass, lightModeClass } from '@/styles/util';

import { Fonts } from '@/ui/Fonts';

import { initializeAmplitudeAnalytics } from '@/utils/amplitude';
import { ThemeProvider } from '@/utils/next-themes';
import { routes } from '@/utils/routes';
import wagmiConfig from '@/utils/wagmi';

import { DEFAULT_SEO_CONFIG } from '../../next-seo.config';
// patch ref: https://github.com/facebook/react/issues/11538#issuecomment-417504600
import '../../node-patch';
import { SankeyProvider } from '../components/SankeyDiagram/SankeyProvider';
import '../global.d.ts';

if (process.env.NODE_ENV !== 'test' && typeof window !== 'undefined') {
  initializeAmplitudeAnalytics();
}

const Disclaimer: DisclaimerComponent = ({ Text, Link }) => (
  <Text>
    <span>By connecting a wallet, you agree to ZeroEx Labs</span>
    <Link href="https://matcha.xyz/terms"> Terms of Service</Link>, and consent to its
    <Link href="https://matcha.xyz/privacy"> Privacy Policy.</Link>
  </Text>
);

const marketingPages: string[] = [
  routes.INDEX,
  routes.CROSS_CHAIN_SWAPS,
  routes.SWAP_TOKENS,
  routes.LIMIT_ORDERS,
  routes.LIQUIDITY_SOURCES,
  routes.ABOUT_MATCHA,
  routes.MATCHA_AUTO,
  routes.NEWSLETTER_SIGNUP,
];

const isMarketingPage = (path: string) => marketingPages.includes(path);

const wagmiConfigChains = wagmiConfig.config.chains.reduce<Record<string, number>>((acc, curr) => {
  acc[curr.name] = curr.id;
  return acc;
}, {});

function MatchaApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [canTrade, setCanTrade] = useState(true);
  const isMobile = useMedia(breakpoints.mobile, false);

  const shouldHideIntercom = isMobile && router.pathname !== routes.INDEX;
  useHideShowIntercom(shouldHideIntercom);

  Sentry.setContext(
    `Wagmi Configured Chains ${typeof window === 'undefined' ? 'server' : 'client'}`,
    wagmiConfigChains,
  );

  const hasContainerHomeStyles = isMarketingPage(router.pathname);

  // referrals
  const { ref } = router.query;
  const { setReferrerId, referrerId, isExpired } = useReferrerStore(referrersStoreSelectors);
  useEffect(() => {
    if (ref && (referrerId !== ref || isExpired())) {
      setReferrerId(ref.toString());
    }
  }, [ref, referrerId, setReferrerId, isExpired]);

  useEffect(() => {
    // Force existing dark mode users to light mode.
    // TODO: MAT-3449 Remove this when dark mode is ready.
    const { localStorage, location } = window;
    if (localStorage.getItem('theme') === 'dark') {
      localStorage.setItem('theme', 'light');
      location.reload();
    }
  }, []);

  useEffect(() => {
    // Ensure decimal-js settings are correct on mount
    Decimal.set({ precision: 80, toExpPos: 1000, toExpNeg: -1000, rounding: Decimal.ROUND_DOWN });
  }, []);

  return (
    <>
      <DefaultSeo {...DEFAULT_SEO_CONFIG} />
      <Fonts />
      <ErrorBoundary>
        <SankeyProvider>
          <ToastPrimitive.Provider>
            <ReactAriaSSRProvider>
              <WagmiProvider config={wagmiConfig.config}>
                <QueryClientProvider client={wagmiConfig.queryClient}>
                  <RainbowKitProvider
                    appInfo={{
                      appName: 'Matcha',
                      disclaimer: Disclaimer,
                      learnMoreUrl: routes.HELP_WALLETS_LEARN_MORE,
                    }}
                  >
                    <ThemeProvider
                      attribute="class"
                      value={{
                        light: lightModeClass,
                        dark: darkModeClass,
                      }}
                      enableSystem={false}
                      defaultTheme={lightModeClass}
                      disableTransitionOnChange
                    >
                      <IntercomProvider>
                        <div
                          className={
                            hasContainerHomeStyles ? containerStyles.home : containerStyles.default
                          }
                          id="app"
                        >
                          <Component {...pageProps} canTrade={canTrade} />
                          <AppUpdater onSetCanTrade={(value: boolean) => setCanTrade(value)} />
                        </div>
                      </IntercomProvider>
                    </ThemeProvider>
                  </RainbowKitProvider>
                  <TransactionUpdater />
                </QueryClientProvider>
              </WagmiProvider>
            </ReactAriaSSRProvider>
          </ToastPrimitive.Provider>
        </SankeyProvider>
      </ErrorBoundary>
      <SpeedInsights sampleRate={0.15} />
    </>
  );
}

export default MatchaApp;
